import React from 'react';
import styled from 'styled-components';
import { useSubmissionsStats } from '../state/hooks';
import { useClassInsights } from './ClassInsightsContext';
import { statsBody } from './useFilters';
import { catalogClasses } from './catalogSelectors';
import { KpiCard } from './KpiCard';
import { EmptyState } from './EmptyState';
import { NO_CLASSES, UNAVAILABLE } from './emptyStateCopy';
import SmallLoader from '../Loader/SmallLoader';

export function KpiSummaryCards() {
  const { debouncedFilters, catalog } = useClassInsights();
  // JED-1066 + JED-1133: gate the stats query on whether any class is in scope
  // (catalog has classes), NOT on classIds.length. Empty classIds means "all
  // classes", which must still fetch; only when there are no classes at all do
  // we skip the query (and show an empty state instead of an infinite loader).
  const hasClassScope = catalogClasses(catalog).length > 0;
  const { data: submissionsData, isLoadingdata, isFetching } = useSubmissionsStats(
    statsBody(debouncedFilters),
    hasClassScope
  );
  const loading = hasClassScope && (isLoadingdata || isFetching);

  const totalSubmissions = submissionsData?.total ?? 0;
  const classworkCount = submissionsData?.classwork ?? 0;
  const independentStudyCount = submissionsData?.independent ?? 0;
  const taskCompletionRate = submissionsData?.taskCompletionPercentage ?? 0;
  const submissionsOnTimeRate = submissionsData?.onTimePercent ?? 0;

  const classworkPercent = totalSubmissions > 0 ? Math.round((classworkCount / totalSubmissions) * 100) : 50;
  const donutPercent = classworkPercent / 2;
  const hasSubmissions = totalSubmissions > 0;

  if (!hasClassScope) {
    const noClassesMessage = catalog?.unavailable ? UNAVAILABLE.message : NO_CLASSES.message;
    return (
      <KpiGrid>
        <KpiCard title="Total submissions">
          <EmptyState compact message={noClassesMessage} />
        </KpiCard>
        <KpiCard title="Task completion rate">
          <EmptyState compact message={noClassesMessage} />
        </KpiCard>
        <KpiCard title="Submissions on time">
          <EmptyState compact message={noClassesMessage} />
        </KpiCard>
      </KpiGrid>
    );
  }

  if (loading) {
    return (
      <KpiGrid>
        <KpiCard title="Total submissions">
          <SmallLoader size={18} label={null} minHeight={80} />
        </KpiCard>
        <KpiCard title="Task completion rate">
          <SmallLoader size={18} label={null} minHeight={80} />
        </KpiCard>
        <KpiCard title="Submissions on time">
          <SmallLoader size={18} label={null} minHeight={80} />
        </KpiCard>
      </KpiGrid>
    );
  }

  return (
    <KpiGrid>
      <KpiCard title="Total submissions">
        <TotalSubmissionsWrap>
          <TotalLabelLeft>
            <span>Classwork</span>
            <strong>({classworkCount})</strong>
          </TotalLabelLeft>
          <DonutChart $classworkPercent={donutPercent} $empty={!hasSubmissions}>
            <DonutChartNumber>{totalSubmissions}</DonutChartNumber>
          </DonutChart>
          <TotalLabelRight>
            <span>Independent Study</span>
            <strong>({independentStudyCount})</strong>
          </TotalLabelRight>
        </TotalSubmissionsWrap>
      </KpiCard>

      <KpiCard title="Task completion rate">
        <KpiValue>{`${taskCompletionRate}%`}</KpiValue>
        <Progress>
          <ProgressFill $width={taskCompletionRate} />
        </Progress>
      </KpiCard>

      <KpiCard title="Submissions on time">
        <KpiValue>{`${submissionsOnTimeRate}%`}</KpiValue>
        <Progress>
          <ProgressFill $width={submissionsOnTimeRate} />
        </Progress>
      </KpiCard>
    </KpiGrid>
  );
}

export default KpiSummaryCards;

const KpiGrid = styled.div.attrs({
  className: 'ci-kpi-grid',
  'data-section': 'summary'
})``;

const TotalSubmissionsWrap = styled.div.attrs({ className: 'ci-total-submissions-wrap' })``;

const TotalLabelLeft = styled.div.attrs({ className: 'ci-total-label-left' })``;

const TotalLabelRight = styled.div.attrs({ className: 'ci-total-label-right' })``;

const DonutChart = styled.div.attrs(({ $empty, $classworkPercent }) => ({
  className: `ci-donut-chart ${$empty ? 'ci-donut-chart--empty' : ''}`.trim(),
  style: { '--classwork-percent': `${$classworkPercent}%` },
}))``;

const DonutChartNumber = styled.div.attrs({ className: 'ci-donut-chart-number' })``;

const KpiValue = styled.div.attrs({ className: 'ci-kpi-value' })``;

const Progress = styled.div.attrs({ className: 'ci-progress' })``;

const ProgressFill = styled.div.attrs({ className: 'ci-progress-fill' })`
  width: ${({ $width }) => `${$width}%`};
`;
